import { MouseEvent, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/** On the grid root while pickup inventories are in bulk edit. */
export const BULK_EDIT_GRID_CLASS = "revivn-bulk-edit";

/**
 * Long enough that a double-click still cancels navigation at the common
 * 500ms OS double-click threshold.
 */
export const EDITABLE_CELL_LINK_DELAY_MS = 500;

const EDITABLE_CELL_CLASS = "MuiDataGrid-cell--editable";

function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

// An editable cell starts its editor on double-click. The first click of that
// double-click would otherwise follow the link and unmount the grid.
export function useEditableCellLink(to: string) {
  const navigate = useNavigate();
  const pending = useRef<{
    timer: ReturnType<typeof setTimeout>;
    cell: Element;
    onDoubleClick: () => void;
  } | null>(null);

  const clearPending = () => {
    const current = pending.current;
    if (!current) return;
    clearTimeout(current.timer);
    current.cell.removeEventListener("dblclick", current.onDoubleClick);
    pending.current = null;
  };

  useEffect(() => {
    return () => {
      const current = pending.current;
      if (!current) return;
      clearTimeout(current.timer);
      current.cell.removeEventListener("dblclick", current.onDoubleClick);
      pending.current = null;
    };
  }, []);

  return {
    onClick(event: MouseEvent<HTMLAnchorElement>) {
      if (!isPlainLeftClick(event)) return;

      const cell = event.currentTarget.closest(".MuiDataGrid-cell");
      if (!cell?.classList.contains(EDITABLE_CELL_CLASS)) return;

      event.preventDefault();
      // Leaving the page during bulk edit would drop unsaved location changes.
      if (cell.closest(`.${BULK_EDIT_GRID_CLASS}`)) return;

      clearPending();
      // detail > 1 is the second click of a double-click, which starts the editor.
      // detail 0 is keyboard activation. That is a single action, so follow the link now.
      if (event.detail > 1) return;
      if (event.detail === 0) {
        navigate(to);
        return;
      }

      const onDoubleClick = () => clearPending();
      cell.addEventListener("dblclick", onDoubleClick);
      const timer = setTimeout(() => {
        cell.removeEventListener("dblclick", onDoubleClick);
        if (pending.current?.timer === timer) pending.current = null;
        navigate(to);
      }, EDITABLE_CELL_LINK_DELAY_MS);
      pending.current = { timer, cell, onDoubleClick };
    },
  };
}
